/**
 * Send a form about a record to anyone with an inbox. Record-type agnostic.
 * Mounted only while open, so every send starts from a clean form.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useFormTemplates, useSendForm } from "@/admin/hooks/use-forms";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { toastApiError } from "@/shared/lib/api-error";
import { useForm, type FormField } from "@/shared/lib/forms";

export function SendFormDialog({
  recordType,
  recordUuid,
  onClose,
}: {
  recordType: string;
  recordUuid: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: templates = [] } = useFormTemplates({ recordType, activeOnly: true });
  const send = useSendForm();

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: "select",
        name: "template_key",
        label: t("forms.send.form", "Form"),
        required: t("forms.send.formRequired", "Pick a form"),
        value: templates.length === 1 ? templates[0].key : "",
        options: templates.map((tpl) => ({ value: tpl.key, label: tpl.name })),
      },
      {
        type: "text",
        name: "recipient_name",
        label: t("forms.send.name", "Their name"),
        value: "",
      },
      {
        type: "email",
        name: "recipient_email",
        label: t("forms.send.email", "Their email"),
        required: t("forms.send.emailRequired", "Email is required"),
        value: "",
        placeholder: "name@company.com",
      },
    ],
    [templates, t],
  );

  const { Form, ref } = useForm(
    { name: "send-form", submit: () => <></>, fields },
    async (values) => {
      try {
        await send.mutateAsync({
          template_key: String(values.template_key),
          record_type: recordType,
          record_uuid: recordUuid,
          recipient_email: String(values.recipient_email).trim(),
          recipient_name: String(values.recipient_name || "").trim(),
        });
        toast.success(t("forms.send.sent", "Form sent."));
        onClose();
      } catch (err) {
        toastApiError(err, t("forms.send.failed", "Could not send the form."));
      }
    },
    [recordType, recordUuid, onClose, t],
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[480px]" data-testid="send-form-dialog">
        <DialogHeader>
          <DialogTitle>{t("forms.send.title", "Send a form")}</DialogTitle>
          <DialogDescription>
            {t(
              "forms.send.hint",
              "They get an email with a link to the form. You'll be notified when they submit it.",
            )}
          </DialogDescription>
        </DialogHeader>
        <Form className="flex flex-col gap-3" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={() => ref.current?.submitForm()}
            disabled={send.isPending}
            data-testid="send-form-submit"
          >
            {send.isPending ? t("forms.send.sending", "Sending...") : t("forms.send.submit", "Send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
